import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ClientData } from '@/types/api';

const KEY = (id?: number) => (id == null ? ['clients'] : ['clients', id]);

type ClientFilters = {
  status?: string;
  q?: string;
  trashed?: 'only' | 'with';
  page?: number;
};

export function useClients(filters: ClientFilters = {}) {
  return useQuery({
    queryKey: ['clients', filters],
    queryFn: async (): Promise<Paginated<ClientData>> => {
      const { data } = await api.get<Paginated<ClientData>>('/v1/clients', { params: filters });
      return data;
    },
  });
}

export function useClient(id: number | undefined) {
  return useQuery({
    queryKey: KEY(id),
    enabled: id != null,
    queryFn: async (): Promise<ClientData> => {
      const { data } = await api.get<ClientData>(`/v1/clients/${id}`);
      return data;
    },
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<ClientData> & { create_stripe_customer?: boolean },
    ): Promise<ClientData> => {
      await primeCsrf();
      const { data } = await api.post<ClientData>('/v1/clients', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<ClientData>): Promise<ClientData> => {
      await primeCsrf();
      const { data } = await api.patch<ClientData>(`/v1/clients/${id}`, patch);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: KEY(id) });
    },
  });
}

export function useDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useRestoreClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ClientData> => {
      await primeCsrf();
      const { data } = await api.post<ClientData>(`/v1/clients/${id}/restore`);
      return data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: KEY(id) });
    },
  });
}

export function useForceDeleteClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${id}/force`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}
